import React, { useMemo, useState } from "react";
import { ArrowDownIcon, ArrowUpIcon, XMarkIcon } from "@heroicons/react/20/solid";
import { useTaxonomies } from "../../hooks/useTaxonomies";
import { useVideosWithEntities } from "../../hooks/useVideosWithEntities";
import { saveTaxonomies, TAXONOMY_LABELS, TaxonomyKey } from "../../lib/taxonomies";
import { notify } from "../../utils/notify";
import { inputClass, primaryButton, Section } from "../admin/ui";

export default function TaxonomiesView() {
  const { taxonomies, setTaxonomies, loading } = useTaxonomies();
  const { videos } = useVideosWithEntities("admin");
  const [drafts, setDrafts] = useState<Record<TaxonomyKey, string>>({ industries: "", formats: "", styles: "" });
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const usage = useMemo(() => {
    const count: Record<TaxonomyKey, Map<string, number>> = {
      industries: new Map(),
      formats: new Map(),
      styles: new Map(),
    };
    videos.forEach((v) =>
      (Object.keys(count) as TaxonomyKey[]).forEach((key) =>
        v[key].forEach((value) => count[key].set(value, (count[key].get(value) ?? 0) + 1)),
      ),
    );
    return count;
  }, [videos]);

  const update = (key: TaxonomyKey, list: string[]) => {
    setTaxonomies((prev) => ({ ...prev, [key]: list }));
    setDirty(true);
  };

  const move = (key: TaxonomyKey, index: number, delta: number) => {
    const list = [...taxonomies[key]];
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    update(key, list);
  };

  const add = (key: TaxonomyKey) => {
    const value = drafts[key].trim();
    if (!value) return;
    if (taxonomies[key].some((v) => v.toLowerCase() === value.toLowerCase())) {
      notify({ isSuccess: false, message: `« ${value} » existe déjà` });
      return;
    }
    update(key, [...taxonomies[key], value]);
    setDrafts((prev) => ({ ...prev, [key]: "" }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await saveTaxonomies(taxonomies);
      setDirty(false);
      notify({ isSuccess: true, message: "Catégories enregistrées", info: "Elles sont utilisées immédiatement par le site et le CMS." });
    } catch (err) {
      notify({ isSuccess: false, message: "Enregistrement impossible", info: err instanceof Error ? err.message : undefined });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-neutral-500 animate-pulse">Chargement…</p>;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-neutral-600 max-w-2xl">
          L'ordre ici est celui des filtres sur le site. Retirer une valeur ne la supprime pas des
          vidéos déjà classées : elle reste visible dans leur fiche avec la mention « hors liste ».
        </p>
        <button className={primaryButton} disabled={!dirty || saving} onClick={save}>
          {saving ? "Enregistrement…" : dirty ? "Enregistrer les catégories" : "Catégories à jour"}
        </button>
      </div>
      <div className="grid lg:grid-cols-3 gap-4">
        {(Object.keys(TAXONOMY_LABELS) as TaxonomyKey[]).map((key) => (
          <Section key={key} title={TAXONOMY_LABELS[key]} description={`${taxonomies[key].length} valeurs`}>
            <ul className="flex flex-col divide-y divide-neutral-100 -my-2">
              {taxonomies[key].map((value, index) => (
                <li key={value} className="flex items-center gap-2 py-1.5">
                  <span className="flex-1 text-sm">{value}</span>
                  <span className="text-xs text-neutral-500 tabular-nums" title="Vidéos classées">
                    {usage[key].get(value) ?? 0}
                  </span>
                  <button className="p-1 text-neutral-400 hover:text-neutral-900 disabled:opacity-30" disabled={index === 0} onClick={() => move(key, index, -1)} aria-label={`Monter ${value}`}>
                    <ArrowUpIcon className="w-4 h-4" />
                  </button>
                  <button className="p-1 text-neutral-400 hover:text-neutral-900 disabled:opacity-30" disabled={index === taxonomies[key].length - 1} onClick={() => move(key, index, 1)} aria-label={`Descendre ${value}`}>
                    <ArrowDownIcon className="w-4 h-4" />
                  </button>
                  <button className="p-1 text-neutral-400 hover:text-red-700" onClick={() => update(key, taxonomies[key].filter((v) => v !== value))} aria-label={`Retirer ${value}`}>
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                add(key);
              }}
            >
              <input
                className={inputClass}
                placeholder="Nouvelle valeur"
                value={drafts[key]}
                onChange={(e) => setDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
              />
              <button type="submit" className={primaryButton}>Ajouter</button>
            </form>
          </Section>
        ))}
      </div>
    </div>
  );
}
